/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ButtonSeverity } from 'primeng/button';

type ButtonIconPosition = 'left' | 'right' | 'top' | 'bottom';

@Component({ changeDetection: ChangeDetectionStrategy.OnPush, template: '' })
export class ButtonPublicApiComponent {
  /**
   * Type of the button.
   * @group Props
   */
  @Input() type = 'button';
  /**
   * Position of the icon.
   * @group Props
   */
  @Input() iconPos: ButtonIconPosition = 'left';
  /**
   * Name of the icon.
   * @group Props
   */
  @Input() icon: string | undefined;
  /**
   * Value of the badge.
   * @group Props
   */
  @Input() badge: string | undefined;
  /**
   * Uses to pass attributes to the label's DOM element.
   * @group Props
   */
  @Input() label: string | undefined;
  /**
   * When present, it specifies that the component should be disabled.
   * @group Props
   */
  @Input() disabled: boolean | undefined;
  /**
   * Whether the button is in loading state.
   * @group Props
   */
  @Input() loading = false;
  /**
   * Icon to display in loading state.
   * @group Props
   */
  @Input() loadingIcon: string | undefined;
  /**
   * Add a shadow to indicate elevation.
   * @group Props
   */
  @Input() raised = false;
  /**
   * Add a circular border radius to the button.
   * @group Props
   */
  @Input() rounded = false;
  /**
   * Add a textual class to the button without a background initially.
   * @group Props
   */
  @Input() text = false;
  /**
   * Defines the style of the button.
   * @group Props
   */
  @Input() severity: ButtonSeverity;
  /**
   * Add a border class without a background initially.
   * @group Props
   */
  @Input() outlined = false;
  /**
   * Add a link style to the button.
   * @group Props
   */
  @Input() link = false;
  /**
   * Add a tabindex to the button.
   * @group Props
   */
  @Input() tabindex: number | undefined;
  /**
   * Defines the size of the button.
   * @group Props
   */
  @Input() size: 'small' | 'large' | undefined;
  /**
   * Specifies the variant of the component.
   * @group Props
   */
  @Input() variant: 'outlined' | 'text' | undefined;
  /**
   * Inline style of the element.
   * @group Props
   */
  @Input() style: { [klass: string]: any } | null | undefined;
  /**
   * Class of the element.
   * @group Props
   */
  @Input() styleClass: string | undefined;
  /**
   * Severity type of the badge.
   * @group Props
   * @defaultValue secondary
   */
  @Input() badgeSeverity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | null | undefined =
    'secondary';
  /**
   * Used to define a string that autocomplete attribute the current element.
   * @group Props
   */
  @Input() ariaLabel: string | undefined;
  /**
   * Identifies the current item within a set of related navigation items.
   * @group Props
   */
  @Input() ariaCurrent: 'page' | 'step' | 'location' | 'date' | 'time' | boolean | undefined;
  /**
   * Indicates the current pressed state of a toggle button.
   * @group Props
   */
  @Input() ariaPressed: boolean | 'mixed' | undefined;
  /**
   * When present, it specifies that the component should automatically get focus on load.
   * @group Props
   */
  @Input() autofocus: boolean | undefined;
  /**
   * Spans 100% width of the container when enabled.
   * @group Props
   */
  @Input() fluid: boolean | undefined;
  /**
   * Callback to execute when button is clicked.
   * This event is intended to be used with the <p-button> component. Using a regular <button> element, use (click).
   * @param {MouseEvent} event - Mouse event.
   * @group Emits
   */
  @Output() onClick: EventEmitter<MouseEvent> = new EventEmitter();
  /**
   * Callback to execute when button is focused.
   * This event is intended to be used with the <p-button> component. Using a regular <button> element, use (focus).
   * @param {FocusEvent} event - Focus event.
   * @group Emits
   */
  @Output() onFocus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  /**
   * Callback to execute when button loses focus.
   * This event is intended to be used with the <p-button> component. Using a regular <button> element, use (blur).
   * @param {FocusEvent} event - Focus event.
   * @group Emits
   */
  @Output() onBlur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
}
